import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Lock, Rocket, CalendarClock, ShieldAlert } from "lucide-react";
import { MONTHS, CURRENT_YEAR, YEAR_OPTIONS, fmtDays } from "@/pages/hr/leave/leaveUtils";

const Check = ({ ok, warn, label, value }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0" data-testid={`readiness-${label.replace(/\s+/g, "-").toLowerCase()}`}>
    <span className="text-sm text-slate-600 flex items-center gap-2">
      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : warn ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <XCircle className="w-4 h-4 text-slate-300" />}
      {label}</span>
    <span className={`text-sm font-medium ${ok ? "text-emerald-700" : warn ? "text-amber-600" : "text-slate-400"}`}>{value}</span>
  </div>
);

export default function LeaveCutoverTab() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [goLive, setGoLive] = useState("");

  const loadStatus = () => api.get("/hr/leave/cutover/status").then((r) => setStatus(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const loadHistory = () => api.get("/hr/leave/cutover/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadStatus(); loadHistory(); }, []);
  useEffect(() => { if (status?.cutover) setGoLive(status.cutover.go_live_date || status.cutover.effective_date || ""); }, [status]);

  const doPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/hr/leave/cutover/preview", { year, month }); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doFinalize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hr/leave/cutover/finalize", { year, month, replace: !!preview?.is_replace });
      toast.success(data.status === "already_finalized" ? "Already finalized for this period" : `Cutover finalized — ${data.opening_posted} opening balances locked`);
      setConfirmOpen(false); setPreview(null); loadStatus(); loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const confirmGoLive = async () => {
    try { await api.post("/hr/leave/cutover/go-live", { effective_date: goLive }); toast.success("ERP Leave go-live date confirmed"); loadStatus(); loadHistory(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!status) return <div className="p-6 text-center text-slate-400">Loading…</div>;
  const finalized = status.opening_status === "finalized";

  return (
    <div data-testid="leave-cutover-tab" className="space-y-5">
      {/* Readiness */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="cutover-readiness">
        <h3 className="font-semibold text-sm mb-2 flex items-center gap-2"><Rocket className="w-4 h-4 text-sky-600" />ERP Leave Go-Live Readiness</h3>
        <Check ok={status.leave_types_ready} label="AutoCount Leave Types" value={status.leave_types_ready ? `${status.leave_types_count} types` : "None"} />
        <Check ok={status.historical_imported} warn={!status.historical_imported} label="Historical Import" value={status.historical_imported ? "Imported" : "Not imported"} />
        <Check ok={finalized} warn={status.opening_status === "draft"} label="Opening Balance" value={finalized ? "Finalized" : status.opening_status === "draft" ? "Draft" : "None"} />
        <Check ok={finalized} warn={!finalized} label="Cutover Period" value={status.opening_period ? `${MONTHS[Number(status.opening_period.slice(5)) - 1]} ${status.opening_period.slice(0, 4)}` : "—"} />
        <Check ok={status.go_live_ready} warn={!status.go_live_ready} label="ERP Leave Go-Live" value={status.go_live_ready ? "Ready" : "Not Ready"} />
      </div>

      {finalized && status.cutover && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="cutover-current">
          <div className="flex items-center gap-2 mb-1"><Lock className="w-4 h-4 text-emerald-700" /><span className="font-semibold text-emerald-900">Leave Cutover — Finalized</span><Badge variant="outline" className="text-emerald-700 border-emerald-300">Locked</Badge></div>
          <div className="text-sm text-emerald-800 grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            <div><div className="text-[11px] opacity-70">Period</div>{MONTHS[status.cutover.cutover_month - 1]} {status.cutover.cutover_year}</div>
            <div><div className="text-[11px] opacity-70">Employees</div>{status.cutover.employee_count}</div>
            <div><div className="text-[11px] opacity-70">Balance Rows</div>{status.cutover.balance_row_count}</div>
            <div><div className="text-[11px] opacity-70">Leave Types</div>{status.cutover.leave_type_count}</div>
            <div className="col-span-2"><div className="text-[11px] opacity-70">Confirmed By</div>{status.cutover.confirmed_by}</div>
            <div className="col-span-2"><div className="text-[11px] opacity-70">Confirmed At</div>{(status.cutover.confirmed_at || "").slice(0, 16).replace("T", " ")}</div>
          </div>
          <div className="mt-3 pt-3 border-t border-emerald-200">
            <div className="text-[11px] opacity-70 mb-1">ERP Leave Go-Live Effective Date</div>
            {status.cutover.go_live_date ? (
              <div className="flex flex-wrap items-center gap-2" data-testid="golive-date-set">
                <Rocket className="w-4 h-4 text-emerald-700" /><b>{status.cutover.go_live_date}</b>
                <Badge variant="outline" className="text-emerald-700 border-emerald-300">Operational</Badge>
                <span className="text-[11px] opacity-70">confirmed by {status.cutover.go_live_confirmed_by}</span>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2" data-testid="golive-date-unset">
                <Input type="date" className="h-8 w-40 bg-white" value={goLive} min={status.cutover.effective_date}
                  onChange={(e) => setGoLive(e.target.value)} data-testid="golive-date-input" />
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmGoLive} data-testid="golive-confirm-btn">Confirm Go-Live Date</Button>
                <span className="text-[11px] text-amber-700">Not set — confirm the exact date ERP Leave becomes operational.</span>
              </div>
            )}
          </div>
        </div>
      )}

      {finalized && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="cutover-reconcile-note">
          <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>New AutoCount leave activity recorded <b>after</b> the ERP cutover ({status.cutover?.as_of_date}) is <b>not</b> automatically included. From the go-live date onward, ERP Leave is the source of truth; any post-cutover AutoCount changes must be explicitly migrated / reconciled.</span>
        </div>
      )}

      {/* Stepper */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="cutover-stepper">
        <h3 className="font-semibold text-sm mb-3">{finalized ? "Replace Opening Balance (audited)" : "Finalize ERP Opening Balance"}</h3>
        <div className="flex flex-wrap gap-3 items-end mb-3">
          <div><Label className="text-xs">Cutover Year</Label>
            <Select value={String(year)} onValueChange={(v) => { setYear(Number(v)); setPreview(null); }}>
              <SelectTrigger className="h-8 w-28" data-testid="cutover-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEAR_OPTIONS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Cutover Month</Label>
            <Select value={String(month)} onValueChange={(v) => { setMonth(Number(v)); setPreview(null); }}>
              <SelectTrigger className="h-8 w-28" data-testid="cutover-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select></div>
          <Button size="sm" variant="outline" onClick={doPreview} disabled={busy} data-testid="cutover-preview-btn"><RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />Fetch &amp; Preview</Button>
        </div>

        {preview && (
          <div className="space-y-3" data-testid="cutover-preview">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-sm flex flex-wrap items-center gap-2" data-testid="cutover-preview-banner">
              <CalendarClock className="w-4 h-4 text-sky-600" />Opening Balance As Of: <b>{MONTHS[month - 1]} {year}</b> · Source: <b>AutoCount HRMS</b>
              <span className="ml-auto flex gap-2">
                <Badge variant="outline" className="text-emerald-700 border-emerald-300">Matched: {preview.summary.matched}</Badge>
                {preview.summary.unmatched_employee_codes?.length > 0 && <Badge variant="outline" className="text-rose-700 border-rose-300">Unmatched: {preview.summary.unmatched_employee_codes.length}</Badge>}
              </span>
            </div>
            {preview.already_finalized && <div className="text-sm text-emerald-700" data-testid="cutover-already">This period is already the finalized ERP opening balance.</div>}
            {preview.will_supersede_period && !preview.already_finalized && (
              <div className="text-xs text-amber-700 flex items-center gap-1.5" data-testid="cutover-supersede-note">
                <AlertTriangle className="w-3.5 h-3.5" />This will supersede the current opening ({preview.will_supersede_period}). The old snapshot is retained for audit with no balance impact (no duplicate credits).</div>
            )}
            {preview.replace_blocked && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700 flex items-start gap-2" data-testid="cutover-blocked">
                <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />{preview.blocked_reason}</div>
            )}

            <div className="max-h-64 overflow-y-auto border border-slate-100 rounded-md">
              <table className="excel-table w-full" data-testid="cutover-preview-table">
                <thead><tr><th>Employee</th><th>Match</th><th>Type</th><th>Entitled</th><th>Taken</th><th>Remaining</th></tr></thead>
                <tbody>{preview.rows.map((r, i) => (
                  <tr key={i} className={r.matched ? "" : "opacity-50"}>
                    <td className="text-xs">{r.employee_name || r.autocount_employee_code}</td>
                    <td className="text-xs font-mono">{r.matched_fas_id ? <span className="text-emerald-700">{r.matched_fas_id}</span> : <span className="text-rose-600">unmatched</span>}{r.employment_status ? ` · ${r.employment_status}` : ""}</td>
                    <td className="text-xs">{r.leave_type_code}</td>
                    <td className="text-center">{fmtDays(r.entitlement)}</td>
                    <td className="text-center">{fmtDays(r.taken)}</td>
                    <td className="text-center font-semibold">{fmtDays(r.balance)}</td>
                  </tr>))}</tbody>
              </table>
            </div>

            {!preview.already_finalized && (
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || preview.replace_blocked} data-testid="cutover-finalize-btn" onClick={() => setConfirmOpen(true)}>
                <Lock className="w-4 h-4 mr-1.5" />{preview.is_replace ? "Replace &amp; Finalize Cutover" : "Finalize &amp; Lock Opening Balance"}</Button>
            )}
          </div>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="cutover-history">
          <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold">Cutover History</div>
          {history.map((c) => (
            <div key={c.id} className="flex items-center justify-between px-4 py-2 border-b border-slate-50 last:border-0 text-xs" data-testid={`cutover-hist-${c.as_of_date}`}>
              <span>{MONTHS[c.cutover_month - 1]} {c.cutover_year} · {c.employee_count} employees · {c.balance_row_count} rows</span>
              <span className="flex items-center gap-2">{c.active ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">Active</Badge> : <Badge variant="outline" className="text-slate-500">Superseded</Badge>}
                <span className="text-slate-400">{(c.confirmed_at || "").slice(0, 10)} · {c.confirmed_by}</span></span>
            </div>
          ))}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="cutover-confirm-dialog">
          <DialogHeader><DialogTitle>Finalize Leave Cutover</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p>You are about to set <b>{MONTHS[month - 1]} {year}</b> as the official ERP opening balance.</p>
            <ul className="list-disc pl-5 text-xs space-y-1">
              <li>This becomes the single balance-impacting opening snapshot.</li>
              <li>Any previous opening is superseded (retained for audit, no balance impact).</li>
              <li>Future AutoCount balance imports become reference-only — the ERP ledger is the source of truth.</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} data-testid="cutover-confirm-btn" onClick={doFinalize}>Confirm &amp; Finalize</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
